import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  Search, Book, Rocket, ClipboardList, Users, Building, 
  Receipt, BarChart3, Globe, Settings, HelpCircle, FileText,
  ChevronRight, Clock, Eye, ArrowLeft, Play, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const iconMap = {
  Rocket, ClipboardList, Users, Building, Receipt, 
  BarChart3, Globe, Settings, HelpCircle, File: FileText, BookOpen
};

const KnowledgeBase = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [modules, setModules] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentArticle, setCurrentArticle] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchModules();
    if (slug) {
      fetchArticle(slug);
    } else {
      fetchArticles();
    }
  }, [slug]);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/categories`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setCategories(await response.json());
      }
    } catch (error) {
      console.error('Failed to load categories');
    }
  };

  const fetchModules = async () => {
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/modules`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setModules(await response.json());
      }
    } catch (error) {
      console.error('Failed to load modules');
    }
  };

  const fetchArticles = async (category = null) => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/knowledge-base/articles`;
      if (category) url += `?category=${category}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setArticles(await response.json());
      }
    } catch (error) {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const fetchArticle = async (articleSlug) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/articles/${articleSlug}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setCurrentArticle(await response.json());
      } else {
        navigate('/knowledge-base');
      }
    } catch (error) {
      toast.error('Failed to load article');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/knowledge-base/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        setSearchResults(await response.json());
      }
    } catch (error) {
      toast.error('Search failed');
    }
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);
    setSearchResults(null);
    fetchArticles(categoryId);
  };

  // Article View
  if (currentArticle) {
    return (
      <div className="max-w-4xl mx-auto space-y-6" data-testid="kb-article-view">
        <Button 
          variant="ghost" 
          onClick={() => { setCurrentArticle(null); navigate('/knowledge-base'); }}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Knowledge Base
        </Button>
        
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="outline">{currentArticle.category}</Badge>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Eye className="h-3 w-3" /> {currentArticle.views} views
              </span>
            </div>
            <CardTitle className="text-2xl">{currentArticle.title}</CardTitle>
            {currentArticle.summary && (
              <CardDescription className="text-base">{currentArticle.summary}</CardDescription>
            )}
          </CardHeader>
          <CardContent>
            {currentArticle.video_url && (
              <div className="mb-6 aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
                <iframe
                  src={currentArticle.video_url.replace('watch?v=', 'embed/')}
                  className="w-full h-full rounded-lg"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title={currentArticle.title}
                />
              </div>
            )}
            
            <div 
              className="prose prose-blue max-w-none"
              dangerouslySetInnerHTML={{ __html: currentArticle.content.replace(/\n/g, '<br/>') }}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="knowledge-base">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Book className="h-6 w-6 text-blue-600" />
            Knowledge Base
          </h1>
          <p className="text-muted-foreground">Learn how to use the Bylyngo platform</p>
        </div>
        
        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 w-full md:w-96">
          <Input
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1"
            data-testid="kb-search"
          />
          <Button type="submit">
            <Search className="h-4 w-4" />
          </Button>
        </form>
      </div>

      {/* Search Results */}
      {searchResults && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              Search Results for "{searchResults.query}" ({searchResults.total_results} results)
            </CardTitle>
          </CardHeader>
          <CardContent>
            {searchResults.articles.length === 0 && searchResults.modules.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No results found</p>
            ) : (
              <div className="space-y-3">
                {searchResults.articles.map((article) => (
                  <div 
                    key={article.article_id}
                    className="p-3 rounded-lg hover:bg-muted cursor-pointer border"
                    onClick={() => navigate(`/knowledge-base/${article.slug}`)}
                  >
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-blue-600" />
                      <span className="font-medium">{article.title}</span>
                      <Badge variant="outline" className="text-xs">{article.category}</Badge>
                    </div>
                    {article.summary && (
                      <p className="text-sm text-muted-foreground mt-1 ml-6">{article.summary}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!searchResults && (
        <>
          {/* Learning Modules */}
          {modules.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-purple-600" />
                Learning Modules
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {modules.map((module) => (
                  <Card 
                    key={module.module_id} 
                    className="cursor-pointer hover:border-purple-300 transition-colors"
                    onClick={() => navigate(`/knowledge-base/module/${module.slug}`)}
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <BookOpen className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold">{module.title}</h3>
                          <p className="text-sm text-muted-foreground line-clamp-2">{module.description}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <FileText className="h-3 w-3" /> {module.articles?.length || 0} articles
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" /> {module.estimated_time_minutes} min
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Categories */}
          <div>
            <h2 className="text-lg font-semibold mb-3">Browse by Category</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
              {categories.map((category) => {
                const Icon = iconMap[category.icon] || FileText;
                return (
                  <Card 
                    key={category.id}
                    className={`cursor-pointer transition-colors hover:border-blue-300 ${selectedCategory === category.id ? 'border-blue-500 bg-blue-50' : ''}`}
                    onClick={() => handleCategoryClick(category.id)}
                  >
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center gap-3">
                        <Icon className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="font-medium text-sm">{category.label}</p>
                          <p className="text-xs text-muted-foreground">{category.article_count} articles</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Articles List */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {selectedCategory ? `${categories.find(c => c.id === selectedCategory)?.label || 'Articles'}` : 'All Articles'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : articles.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Book className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>No articles found</p>
                  <p className="text-sm">Check back later for new content</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {articles.map((article) => (
                    <div 
                      key={article.article_id}
                      className="flex items-center justify-between p-3 rounded-lg hover:bg-muted cursor-pointer group"
                      onClick={() => navigate(`/knowledge-base/${article.slug}`)}
                      data-testid={`kb-article-${article.slug}`}
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="font-medium group-hover:text-blue-600">{article.title}</p>
                          {article.summary && (
                            <p className="text-sm text-muted-foreground line-clamp-1">{article.summary}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {article.video_url && (
                          <Badge variant="outline" className="text-xs">
                            <Play className="h-3 w-3 mr-1" /> Video
                          </Badge>
                        )}
                        <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-blue-600" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default KnowledgeBase;
